import { useState, useEffect } from "react";
import useStatusParam from "../../hooks/useStatusParam";
import apiClient from "../../api-services/ApiClient";
import AdminLayout from "../../app-components/AdminLayout";
import Alert from "../../app-components/alert/Alert";
import ActiveFilters, {
  FilterChip,
} from "../../app-components/ui/ActiveFilters";
import ColumnMenu, {
  ColumnDef,
  useColumnVisibility,
} from "../../app-components/ui/ColumnMenu";
import EmptyState from "../../app-components/ui/EmptyState";
import PageHeader from "../../app-components/ui/PageHeader";
import Pagination from "../../app-components/ui/Pagination";
import StatusPill from "../../app-components/ui/StatusPill";
import { btnPrimary, card, inputBase } from "../../app-components/ui/styles";
import TableSkeleton from "../../app-components/ui/TableSkeleton";
import { Icon_Dots } from "../../assets/Icons";
import { ParamInterface } from "../../interface/users.interface";
import { formatDate } from "../../utils";
import { APP_STORAGE } from "../../api-services/Process";

const TABS = [
  { value: "", label: "All Requests" },
  { value: "pending", label: "Pending" },
  { value: "closed", label: "Closed" },
  { value: "accepted", label: "Accepted" },
  { value: "rejected", label: "Rejected" },
];

// Optional columns the admin can show or hide. Full Name and Actions are always shown.
const COLUMNS: ColumnDef[] = [
  { key: "sn", label: "S/N" },
  { key: "media", label: "Caption" },
  { key: "username", label: "Username" },
  { key: "gender", label: "Gender", defaultVisible: false },
  { key: "interest", label: "Interest" },
  { key: "text", label: "Prespecified Text", defaultVisible: false },
  { key: "country", label: "Country" },
  { key: "state", label: "State" },
  { key: "city", label: "City", defaultVisible: false },
  { key: "subscription", label: "Subscription" },
  { key: "status", label: "Status" },
  { key: "created", label: "Created At" },
];

const th =
  "py-3 px-4 text-left text-xs font-semibold uppercase tracking-wide text-hcMuted bg-hcHead sticky top-0 z-10 whitespace-nowrap";
const td = "py-3 px-4 text-left text-sm whitespace-nowrap";

export interface ConnectionRequestType {
  id: number;
  uuid: string;
  user_id: number;
  prespecified_text_id: number;
  expected_response: string;
  status: string;
  interest_id: number;
  caption_image: string | null;
  city_id: number;
  state_id: number;
  country_id: number;
  responders_group: string | null;
  created_at: string;
  updated_at: string;
  country: {
    id: number;
    name: string;
    iso3: string;
    numeric_code: string;
    iso2: string;
    phonecode: string;
    capital: string;
    currency: string;
    currency_name: string;
    currency_symbol: string;
    tld: string;
    native: string;
    region: string;
    region_id: number;
    subregion: string;
    subregion_id: number;
    nationality: string;
    timezones: string;
    translations: string;
    latitude: string;
    longitude: string;
    emoji: string;
    emojiU: string;
    created_at: string;
    updated_at: string;
    flag: number;
    wikiDataId: string;
  };
  state: {
    id: number;
    name: string;
    country_id: number;
    country_code: string;
    fips_code: string;
    iso2: string;
    type: string | null;
    latitude: string;
    longitude: string;
    created_at: string;
    updated_at: string;
    flag: number;
    wikiDataId: string;
  };
  city: {
    id: number;
    name: string;
    state_id: number;
    state_code: string;
    country_id: number;
    country_code: string;
    latitude: string;
    longitude: string;
    created_at: string;
    updated_at: string;
    flag: number;
    wikiDataId: string;
  };
  interest: {
    id: number;
    interest: string;
    created_at: string | null;
    updated_at: string | null;
  };
  prespecified_text: {
    id: number;
    content: string;
    created_at: string | null;
    updated_at: string | null;
  };
  user: {
    id: number;
    full_name: string;
    email: string;
    email_verified_at: string;
    is_email_verified: string;
    is_phone_verified: string;
    username: string;
    DOB: string;
    gender: string;
    country_id: number;
    state_id: number;
    city_id: number;
    area: string;
    phone_number: string;
    phone_verified_at: string;
    instagram_id: string | null;
    snapchat_id: string | null;
    twitter_x_id: string | null;
    profile_pix_link: string;
    verification_code: string | null;
    bio: string | null;
    is_verified: number;
    badge: string | null;
    interest: string | null;
    is_private: number;
    is_email_notification: number;
    created_at: string;
    updated_at: string;
    last_login: string | null;
    deleted_at: string | null;
    status: string | null;
    active_subscription: {
      id: number;
      user_id: number;
      subscription_plan: string;
      subscription_plan_id: number | null;
      subscription_plan_duration: number;
      connection_request_uuid: string | null;
      status: string;
      start_date: string;
      end_date: string;
      created_at: string;
      updated_at: string;
    };
  };
}

// Inline SVG placeholder (grey media icon) — works offline, no broken icon
const PLACEHOLDER_MEDIA =
  "data:image/svg+xml;utf8," +
  encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40">
      <rect width="40" height="40" rx="20" fill="#E4E1E1"/>
      <path d="M13 27l5-6 3 4 4-5 5 7H13z" fill="#B0AEAE"/>
      <circle cx="16" cy="15" r="3" fill="#B0AEAE"/>
    </svg>`
  );

const isVideo = (filePath: string): boolean => {
  const videoExtensions = [".mp4", ".mov", ".webm", ".ogg"];
  return videoExtensions.some((ext) => filePath.toLowerCase().endsWith(ext));
};

const buildUrl = (path: string | null, isGallery: boolean): string => {
  if (!path) return "";
  const galleryFile = isGallery ? "gallery_pictures/" : "";
  return `${APP_STORAGE}${galleryFile}${path}`;
};

// Media cell with graceful fallback to placeholder (null value OR load failure)
const MediaCell = ({
  filePath,
  isGallery,
}: {
  filePath: string | null;
  isGallery: boolean;
}) => {
  const initialUrl = buildUrl(filePath, isGallery);
  const [src, setSrc] = useState<string>(initialUrl || PLACEHOLDER_MEDIA);
  const [isBroken, setIsBroken] = useState<boolean>(!initialUrl);

  // Keep in sync when the underlying value changes (e.g. pagination)
  useEffect(() => {
    const url = buildUrl(filePath, isGallery);
    setSrc(url || PLACEHOLDER_MEDIA);
    setIsBroken(!url);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filePath, isGallery]);

  if (!filePath || isBroken) {
    return (
      <img
        src={PLACEHOLDER_MEDIA}
        alt="no media"
        className="w-10 h-10 object-cover rounded-full shadow-sm"
      />
    );
  }

  const fullUrl = buildUrl(filePath, isGallery);

  return isVideo(filePath) ? (
    <video controls className="w-16 h-10 object-cover rounded shadow-sm">
      <source src={fullUrl} type="video/mp4" />
      Your browser does not support the video tag.
    </video>
  ) : (
    <img
      src={src}
      alt="media"
      onError={() => {
        setIsBroken(true);
        setSrc(PLACEHOLDER_MEDIA);
      }}
      className="w-10 h-10 object-cover rounded-full shadow-sm"
    />
  );
};

const Request = () => {
  const [pageData, setData] = useState<ConnectionRequestType[]>([]);

  const [currentPage, setCurrentPage] = useState(1);
  const [lastPage, setLastPage] = useState(0);
  // status tab lives in the URL (?status=...) so sidebar submenus can link to it
  const [pageFilter, setPageFilter] = useStatusParam();
  // back to page 1 whenever the status changes (tab click or sidebar link)
  useEffect(() => setCurrentPage(1), [pageFilter]);

  const [searchInput, setSearchInput] = useState<string>("");
  const [searchInputDebounce, setSearchInputDebounce] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [hasError, setHasError] = useState<boolean>(false);

  const { isVisible, toggle, visibleCount } = useColumnVisibility(
    "cols_requests",
    COLUMNS
  );
  const show = (key: string) => isVisible(key);

  // effect for get users list
  useEffect(() => {
    const params = {
      page: currentPage,
      status: pageFilter,
      search: searchInput,
    };

    getRequestData(params);
  }, [currentPage, pageFilter, searchInput]);

  // debounce search input
  useEffect(() => {
    const handler = setTimeout(() => {
      setSearchInput(searchInputDebounce);
      setCurrentPage(1);
    }, 1000);

    return () => {
      clearTimeout(handler);
    };
  }, [searchInputDebounce]);

  const getRequestData = async (params: ParamInterface) => {
    setLoading(true);
    setHasError(false);

    try {
      const res: any = await apiClient.get("admin-get-all-requests", {
        params,
      });
      if (res && res.data) {
        setData(res.data.data);
        setCurrentPage(res.data.current_page);
        setLastPage(res.data.last_page);
      } else {
        setData([]);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      setAlertMessage("Unable to fetch data at the moment.");
      setHasError(true);
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (id: string) => {
    try {
      setLoading(true); // Start loading state

      // Create a copy of
      const previouspageData = [...pageData];

      // Optimistically update the UI
      const updatedpageData = pageData.filter((item: any) => item.uuid !== id);
      setData(updatedpageData);

      // Make the API call
      const res = await apiClient.patch(`close-request-by-admin/${id}`);

      // Check if the response is successful
      if (res && res.status === 200) {
        alert("updated");
        setLoading(false);
      } else {
        setData(previouspageData);
      }
    } catch (error) {
      alert("error");
      setLoading(false);
      console.error("Error while updating status:", error);
    }
  };

  const hasRecords = pageData?.length > 0;

  // Compute the starting serial number for the current page (page 1 -> 1, page 2 -> 11, ...)
  const serialStart = (currentPage - 1) * 10 + 1;


  const chips: FilterChip[] = [];
  if (searchInput)
    chips.push({
      label: `Search: ${searchInput}`,
      onRemove: () => {
        setSearchInputDebounce("");
        setSearchInput("");
        setCurrentPage(1);
      },
    });

  const clearFilters = () => {
    setSearchInputDebounce("");
    setSearchInput("");
    setCurrentPage(1);
  };

  return (
    <AdminLayout title="Admin | Connection Requests">
      <PageHeader
        title="Connection Requests"
        subtitle="Requests members have posted"
      />

      <div className={`${card} py-5`}>
        {/* tabs */}
        <div className="px-5 flex items-center gap-1 mb-5 border-b border-hcBorder">
          {TABS.map((t) => (
            <button
              key={t.value}
              onClick={() => {
                setPageFilter(t.value);
                setCurrentPage(1);
              }}
              className={`px-4 pb-3 text-sm font-medium -mb-px border-b-2 transition ${
                pageFilter === t.value
                  ? "text-honeConnectPurple border-honeConnectPurple"
                  : "text-honeConnectGray border-transparent hover:text-honeConnectBlack"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* filter bar */}
        <div className="px-5 flex items-center gap-3 flex-wrap mb-4">
          <div className="relative flex-1 min-w-[240px]">
            <span className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
              <svg
                className="h-4 w-4 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeWidth="2"
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                ></path>
              </svg>
            </span>
            <input
              value={searchInputDebounce}
              onChange={(e) => setSearchInputDebounce(e.target.value)}
              type="text"
              className={`${inputBase} pl-9 w-full`}
              placeholder="Search by name, username, unique number, location or interest"
            />
          </div>

          <ColumnMenu columns={COLUMNS} isVisible={isVisible} onToggle={toggle} />
        </div>

        <ActiveFilters chips={chips} onClearAll={clearFilters} />

        {/* record table */}
        {hasError && !loading ? (
          <div className="flex flex-col items-center justify-center py-16 px-6 text-center border-y border-hcBorder">
            <div className="w-14 h-14 rounded-full bg-red-50 flex items-center justify-center mb-4">
              <svg
                className="w-7 h-7 text-red-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <p className="font-semibold text-honeConnectBlack mb-1">
              Something went wrong
            </p>
            <p className="text-sm text-hcMuted mb-4">
              We couldn't load connection requests. Please try again.
            </p>
            <button
              onClick={() =>
                getRequestData({
                  page: currentPage,
                  status: pageFilter,
                  search: searchInput,
                } as ParamInterface)
              }
              className={btnPrimary}
            >
              Retry
            </button>
          </div>
        ) : (
          <div className="overflow-auto max-h-[68vh] border-y border-hcBorder">
            <table className="min-w-full bg-white">
              <thead>
                <tr>
                  {show("sn") && <th className={th}>S/N</th>}
                  {show("media") && <th className={th}>Caption</th>}
                  <th
                    className={`${th} left-0 z-20`}
                    style={{ position: "sticky" }}
                  >
                    Full Name
                  </th>
                  {show("username") && <th className={th}>Username</th>}
                  {show("gender") && <th className={th}>Gender</th>}
                  {show("interest") && <th className={th}>Interest</th>}
                  {show("text") && <th className={th}>Prespecified Text</th>}
                  {show("country") && <th className={th}>Country</th>}
                  {show("state") && <th className={th}>State</th>}
                  {show("city") && <th className={th}>City</th>}
                  {show("subscription") && (
                    <th className={th}>Subscription</th>
                  )}
                  {show("status") && <th className={th}>Status</th>}
                  {show("created") && <th className={th}>Created At</th>}
                  <th className={`${th} text-center`}>Actions</th>
                </tr>
              </thead>

              {loading ? (
                <TableSkeleton columns={visibleCount + 2} />
              ) : (
                <tbody className="text-honeConnectBlack">
                  {pageData?.map(
                    (data: ConnectionRequestType, index: number) => {
                      const mediaPath =
                        data.caption_image ??
                        data.user?.profile_pix_link ??
                        null;

                      return (
                        <tr
                          key={data.uuid}
                          className="group border-b border-hcBorder hover:bg-hcBg"
                        >
                          {show("sn") && (
                            <td className={`${td} text-hcMuted`}>
                              {serialStart + index}
                            </td>
                          )}

                          {show("media") && (
                            <td className={td}>
                              <MediaCell
                                filePath={mediaPath}
                                isGallery={!!data.caption_image}
                              />
                            </td>
                          )}

                          <td
                            className={`${td} font-medium bg-white group-hover:bg-hcBg z-[1]`}
                            style={{ position: "sticky", left: 0 }}
                          >
                            {data.user?.full_name}
                          </td>

                          {show("username") && (
                            <td className={td}>{data.user?.username}</td>
                          )}

                          {show("gender") && (
                            <td className={`${td} capitalize`}>
                              {data.user?.gender}
                            </td>
                          )}

                          {show("interest") && (
                            <td className={td}>{data.interest?.interest}</td>
                          )}

                          {show("text") && (
                            <td className={`${td} max-w-[260px] truncate`}>
                              {data.prespecified_text?.content}
                            </td>
                          )}

                          {show("country") && (
                            <td className={td}>{data.country?.name}</td>
                          )}

                          {show("state") && (
                            <td className={td}>{data.state?.name}</td>
                          )}

                          {show("city") && (
                            <td className={td}>{data.city?.name}</td>
                          )}

                          {show("subscription") && (
                            <td className={td}>
                              {data.user?.active_subscription
                                ?.subscription_plan ? (
                                <StatusPill
                                  tone="purple"
                                  label={
                                    data.user.active_subscription
                                      .subscription_plan
                                  }
                                />
                              ) : (
                                <StatusPill label="N/A" tone="neutral" />
                              )}
                            </td>
                          )}

                          {show("status") && (
                            <td className={td}>
                              {data.status && (
                                <StatusPill label={data.status} />
                              )}
                            </td>
                          )}

                          {show("created") && (
                            <td className={td}>{formatDate(data.created_at)}</td>
                          )}

                          <td className={`${td} text-center`}>
                            <div className="dropdown relative">
                              <button
                                title="More Options"
                                className="text-blue-600 dropbtn p-1 rounded hover:bg-hcBg"
                              >
                                <img
                                  src={Icon_Dots}
                                  alt=""
                                  className="text-center"
                                />
                              </button>
                              <div
                                className="dropdown-content absolute shadow-pop bg-white rounded-xl z-30 w-[120px] border border-hcBorder overflow-hidden"
                                style={{ right: "0" }}
                              >
                                <button
                                  className="block p-2 text-center w-full hover:bg-honeConnectPurple hover:text-white"
                                  onClick={() => handleStatusUpdate(data.uuid)}
                                >
                                  Close
                                </button>
                              </div>
                            </div>
                          </td>
                        </tr>
                      );
                    }
                  )}
                </tbody>
              )}
            </table>

            {!loading && !hasRecords && (
              <EmptyState
                title="No requests found"
                message={
                  chips.length || pageFilter
                    ? "Try changing your search or filters."
                    : "There are no connection requests to display yet."
                }
                onClear={chips.length ? clearFilters : undefined}
              />
            )}
          </div>
        )}

        {hasRecords && lastPage > 1 && (
          <Pagination
            currentPage={currentPage}
            lastPage={lastPage}
            onPageChange={setCurrentPage}
          />
        )}
      </div>

      <Alert message={alertMessage} />
    </AdminLayout>
  );
};

export default Request;
